import {
  useHttpClient,
  useNetworkStatus,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import {
  AppointmentHistory,
  AppointmentHistoryDetail,
  AppointmentHistoryFilter,
} from './appointmentHistory.models';
import {
  appointmentHistoryReducer,
  AppointmentHistoryState,
  initAppointmentHistoryState,
} from './appointmentHistory.reducer';
import {
  appendProposalStatus,
  filterAppointments,
  sortAppointments,
} from './appointmentHistory.utils';

export interface AppointmentHistoryContextProps {
  appointmentHistory: AppointmentHistory[];
  currentAppointment: AppointmentHistoryDetail | undefined;
  currentAppointmentFilter: AppointmentHistoryFilter;
  isLoading: boolean;
  isLoaded: boolean;
  isLoadingAppointmentId: string | null;
  error: string | null;
  updateHistoryFilter: (filter: Partial<AppointmentHistoryFilter>) => void;
  fetchAppointmentHistory: (
    filter: Partial<AppointmentHistoryFilter>,
    options?: { signal?: AbortSignal }
  ) => Promise<void>;
  fetchAppointmentDetail: (
    appointmentId: string,
    appointmentProposalId: string
  ) => Promise<AppointmentHistoryDetail>;
  setLoadingAppointmentId: (appointmentId: string | null) => void;
}

const AppointmentHistoryContext = createContext<
  AppointmentHistoryContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<AppointmentHistoryState>;
}>;

export const AppointmentHistoryProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(appointmentHistoryReducer, {
    ...initAppointmentHistoryState,
    ...initialState,
  });
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    if (state.error) {
      showToast(state.error, 'error');
    }
  }, [state.error, showToast]);

  const appointmentHistory = useMemo(() => {
    const appointments = state.ids.map((id) => state.proposalEntities[id]);
    const filtered = filterAppointments(
      appendProposalStatus(appointments),
      state.currentAppointmentFilter
    );
    return sortAppointments(filtered, state.currentAppointmentFilter.sorting);
  }, [state.ids, state.proposalEntities, state.currentAppointmentFilter]);

  const currentAppointment = useMemo(
    () =>
      state.currentAppointmentId
        ? state.proposalDetailsEntities[state.currentAppointmentId]
        : undefined,
    [state.currentAppointmentId, state.proposalDetailsEntities]
  );

  const fetchAppointmentHistory: AppointmentHistoryContextProps['fetchAppointmentHistory'] =
    useCallback(
      async (filter, options) => {
        if (!isOnline()) {
          dispatch({
            type: 'SET_LOADING_STATE',
            payload: { isLoading: false, isLoaded: true },
          });
          return;
        }
        dispatch({ type: 'FETCH_APPOINTMENT_HISTORY', payload: { filter } });
        try {
          const appointmentHistory = await httpClient.get<AppointmentHistory[]>(
            '/service-history/appointments',
            {
              params: {
                startDateTime: filter.startDateTime,
                endDateTime: filter.endDateTime,
                status: filter.status,
                businessUnitIds: filter.businessUnitIds,
                assignedUserIds: filter.assignedUserIds,
                includeNonAirshipJobs: filter.includeNonAirshipJobs
                  ? 'true'
                  : 'false',
              },
              signal: options?.signal,
            }
          );
          dispatch({
            type: 'FETCH_APPOINTMENT_HISTORY_SUCCESS',
            payload: { appointmentHistory },
          });
        } catch (error) {
          if (error instanceof Error && error.message !== 'canceled') {
            dispatch({
              type: 'FETCH_APPOINTMENT_HISTORY_FAILURE',
              payload: { error: `${error}` },
            });
          }
        }
      },
      [httpClient, isOnline]
    );

  const fetchAppointmentDetail: AppointmentHistoryContextProps['fetchAppointmentDetail'] =
    useCallback(
      async (appointmentId, appointmentProposalId) => {
        if (!isOnline()) {
          dispatch({
            type: 'SET_LOADING_STATE',
            payload: { isLoading: false, isLoaded: true },
          });
          return Promise.reject(new Error('Offline'));
        }
        dispatch({
          type: 'FETCH_APPOINTMENT_DETAIL',
          payload: { appointmentId },
        });
        try {
          const appointmentHistoryDetail =
            await httpClient.get<AppointmentHistoryDetail>(
              `/service-history/appointment-proposal?appointmentId=${appointmentId}&appointmentProposalId=${appointmentProposalId}`
            );
          dispatch({
            type: 'FETCH_APPOINTMENT_DETAIL_SUCCESS',
            payload: { appointmentHistoryDetail },
          });
          return appointmentHistoryDetail;
        } catch (error) {
          dispatch({
            type: 'FETCH_APPOINTMENT_DETAIL_FAILURE',
            payload: { error: `${error}` },
          });
          throw error;
        }
      },
      [httpClient, isOnline]
    );

  const setLoadingAppointmentId: AppointmentHistoryContextProps['setLoadingAppointmentId'] =
    useCallback(
      (appointmentId) =>
        dispatch({
          type: 'SET_LOADING_APPOINTMENT_ID',
          payload: { appointmentId },
        }),
      []
    );

  const updateHistoryFilter: AppointmentHistoryContextProps['updateHistoryFilter'] =
    useCallback(
      (filterOptions) =>
        dispatch({
          type: 'UPDATE_APPOINTMENT_HISTORY_FILTER',
          payload: { filterOptions },
        }),
      []
    );

  return (
    <AppointmentHistoryContext.Provider
      value={{
        appointmentHistory,
        currentAppointment,
        currentAppointmentFilter: state.currentAppointmentFilter,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        isLoadingAppointmentId: state.isLoadingAppointmentId,
        error: state.error,
        updateHistoryFilter,
        fetchAppointmentHistory,
        fetchAppointmentDetail,
        setLoadingAppointmentId,
      }}
    >
      {children}
    </AppointmentHistoryContext.Provider>
  );
};

export const useAppointmentHistory = () => {
  const context = useContext(AppointmentHistoryContext);
  if (!context) {
    throw new Error(
      'useAppointmentHistory must be used within an AppointmentHistoryProvider'
    );
  }
  return context;
};
